/**
 * Number format catalogue.
 *
 * One source of truth for every number/currency format a `number` (or
 * number-returning formula/rollup) column can carry. Mirrored verbatim in
 * closot-api (`shared/lib/numberFormats.ts`) and surfaced over MCP by
 * closot-mcp — keep the three in step when adding an entry.
 *
 * Ids match Notion's `number.format` vocabulary so a Notion import can map
 * straight across with no translation table beyond the legacy aliases below.
 */

export type NumberFormatPosition = "prefix" | "suffix" | "none";

export interface NumberFormatDef {
  /** Stable id persisted on PropertySchema.numberFormat. */
  id: string;
  /** Human label for pickers. */
  label: string;
  /** Rendered symbol; "" when the format adds no symbol. */
  symbol: string;
  /** Where the symbol sits relative to the digits. */
  position: NumberFormatPosition;
  /** ISO-4217 code, when the format is a real currency. */
  currency?: string;
  /** Search keywords for the UI picker (country / currency aliases). */
  keywords?: string[];
}

export const NUMBER_FORMATS: readonly NumberFormatDef[] = [
  { id: "number", label: "Number", symbol: "", position: "none", keywords: ["plain"] },
  { id: "number_with_commas", label: "Number with commas", symbol: "", position: "none", keywords: ["comma", "grouped", "thousands"] },
  { id: "percent", label: "Percent", symbol: "%", position: "suffix", keywords: ["percentage"] },

  { id: "dollar", label: "Dollar", symbol: "$", position: "prefix", currency: "USD", keywords: ["usd", "us", "united states", "american"] },
  { id: "canadian_dollar", label: "Canadian dollar", symbol: "CA$", position: "prefix", currency: "CAD", keywords: ["cad", "canada"] },
  { id: "australian_dollar", label: "Australian dollar", symbol: "A$", position: "prefix", currency: "AUD", keywords: ["aud", "australia"] },
  { id: "singapore_dollar", label: "Singapore dollar", symbol: "S$", position: "prefix", currency: "SGD", keywords: ["sgd", "singapore"] },
  { id: "hong_kong_dollar", label: "Hong Kong dollar", symbol: "HK$", position: "prefix", currency: "HKD", keywords: ["hkd", "hong kong"] },
  { id: "new_zealand_dollar", label: "New Zealand dollar", symbol: "NZ$", position: "prefix", currency: "NZD", keywords: ["nzd", "new zealand"] },
  { id: "new_taiwan_dollar", label: "New Taiwan dollar", symbol: "NT$", position: "prefix", currency: "TWD", keywords: ["twd", "taiwan"] },

  { id: "euro", label: "Euro", symbol: "€", position: "prefix", currency: "EUR", keywords: ["eur", "europe"] },
  { id: "pound", label: "Pound", symbol: "£", position: "prefix", currency: "GBP", keywords: ["gbp", "sterling", "uk", "britain"] },
  { id: "yen", label: "Yen", symbol: "¥", position: "prefix", currency: "JPY", keywords: ["jpy", "japan"] },
  { id: "yuan", label: "Yuan", symbol: "CN¥", position: "prefix", currency: "CNY", keywords: ["cny", "rmb", "china", "renminbi"] },
  { id: "won", label: "Won", symbol: "₩", position: "prefix", currency: "KRW", keywords: ["krw", "korea"] },
  { id: "rupee", label: "Rupee", symbol: "₹", position: "prefix", currency: "INR", keywords: ["inr", "india"] },
  { id: "ruble", label: "Ruble", symbol: "₽", position: "prefix", currency: "RUB", keywords: ["rub", "russia"] },
  { id: "real", label: "Real", symbol: "R$", position: "prefix", currency: "BRL", keywords: ["brl", "brazil"] },
  { id: "lira", label: "Lira", symbol: "₺", position: "prefix", currency: "TRY", keywords: ["try", "turkey", "turkish"] },
  { id: "rupiah", label: "Rupiah", symbol: "Rp", position: "prefix", currency: "IDR", keywords: ["idr", "indonesia"] },
  { id: "franc", label: "Franc", symbol: "CHF", position: "prefix", currency: "CHF", keywords: ["chf", "swiss", "switzerland"] },
  { id: "shekel", label: "Shekel", symbol: "₪", position: "prefix", currency: "ILS", keywords: ["ils", "israel"] },
  { id: "baht", label: "Baht", symbol: "฿", position: "prefix", currency: "THB", keywords: ["thb", "thailand"] },
  { id: "ringgit", label: "Ringgit", symbol: "RM", position: "prefix", currency: "MYR", keywords: ["myr", "malaysia"] },
  { id: "dirham", label: "Dirham", symbol: "AED", position: "prefix", currency: "AED", keywords: ["aed", "uae", "emirates"] },
  { id: "riyal", label: "Riyal", symbol: "SAR", position: "prefix", currency: "SAR", keywords: ["sar", "saudi"] },
  { id: "rand", label: "Rand", symbol: "R", position: "prefix", currency: "ZAR", keywords: ["zar", "south africa"] },

  { id: "krona", label: "Krona", symbol: "kr", position: "suffix", currency: "SEK", keywords: ["sek", "sweden", "swedish"] },
  { id: "norwegian_krone", label: "Norwegian krone", symbol: "kr", position: "suffix", currency: "NOK", keywords: ["nok", "norway"] },
  { id: "danish_krone", label: "Danish krone", symbol: "kr", position: "suffix", currency: "DKK", keywords: ["dkk", "denmark"] },
  { id: "zloty", label: "Zloty", symbol: "zł", position: "suffix", currency: "PLN", keywords: ["pln", "poland", "polish"] },
  { id: "forint", label: "Forint", symbol: "Ft", position: "suffix", currency: "HUF", keywords: ["huf", "hungary"] },
  { id: "koruna", label: "Koruna", symbol: "Kč", position: "suffix", currency: "CZK", keywords: ["czk", "czech"] },
  { id: "leu", label: "Leu", symbol: "L", position: "suffix", currency: "RON", keywords: ["ron", "romania"] },

  { id: "mexican_peso", label: "Mexican peso", symbol: "MX$", position: "prefix", currency: "MXN", keywords: ["mxn", "mexico"] },
  { id: "chilean_peso", label: "Chilean peso", symbol: "CLP$", position: "prefix", currency: "CLP", keywords: ["clp", "chile"] },
  { id: "colombian_peso", label: "Colombian peso", symbol: "COP$", position: "prefix", currency: "COP", keywords: ["cop", "colombia"] },
  { id: "philippine_peso", label: "Philippine peso", symbol: "₱", position: "prefix", currency: "PHP", keywords: ["php", "philippines"] },
  { id: "argentine_peso", label: "Argentine peso", symbol: "ARS$", position: "prefix", currency: "ARS", keywords: ["ars", "argentina"] },
  { id: "uruguayan_peso", label: "Uruguayan peso", symbol: "UYU$", position: "prefix", currency: "UYU", keywords: ["uyu", "uruguay"] },
  { id: "peruvian_sol", label: "Peruvian sol", symbol: "S/", position: "prefix", currency: "PEN", keywords: ["pen", "peru"] },
] as const;

export const NUMBER_FORMAT_IDS: readonly string[] = NUMBER_FORMATS.map((f) => f.id);

const BY_ID = new Map(NUMBER_FORMATS.map((f) => [f.id, f]));

/**
 * Pre-catalogue values that still exist in stored schemas. "currency" was the
 * original catch-all and always rendered "$", so it resolves to `dollar`.
 */
const LEGACY_ALIASES: Record<string, string> = {
  currency: "dollar",
  usd: "dollar",
  plain: "number",
  comma: "number_with_commas",
};

/** True when `value` is a catalogue id (legacy aliases excluded). */
export function isNumberFormatId(value: unknown): value is string {
  return typeof value === "string" && BY_ID.has(value);
}

/**
 * Canonical id for anything a client or an old record might hand us, or
 * `undefined` when it is unrecognised (caller should then leave the field
 * untouched rather than writing a bad value).
 */
export function normalizeNumberFormat(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const raw = value.trim();
  if (!raw) return undefined;
  if (BY_ID.has(raw)) return raw;
  const lowered = raw.toLowerCase();
  if (BY_ID.has(lowered)) return lowered;
  return LEGACY_ALIASES[lowered];
}

/** Catalogue entry for an id, resolving legacy aliases. */
export function getNumberFormat(value: unknown): NumberFormatDef | undefined {
  const id = normalizeNumberFormat(value);
  return id ? BY_ID.get(id) : undefined;
}
